import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { FiArrowUpRight } from "react-icons/fi";
import projects from "@/data/projects"
import { ApplicationName, AppState } from "@/lib/types"
import useMethodStore, { useMethodActions } from "@/store/MethodStore"
import useSettingsStore from "@/store/SettingsStore"
import { useEffect } from "react"
import { FaCode, FaGithub, FaLinkedin, FaXTwitter } from "react-icons/fa6";
import { FaBrush, FaMusic, FaPalette } from "react-icons/fa";
import { IoChatbubble } from "react-icons/io5";
import { MdPerson4, MdSettings, MdWidgets } from "react-icons/md";
import useBrowserStore from "@/store/browserStore";
import games from "@/data/games";
import useApps from "@/hooks/useApps";
import useShortcuts from "@/hooks/useShortcuts";

function SearchBar() {
  const {
    setIsSearchBarOpen,
    setSettingsState,
    setFocusedWindow,
    setBrowserState,
  } = useMethodActions()
  const isSearchBarOpen = useMethodStore(state => state.isSearchBarOpen)
  const setLink = useBrowserStore(state => state.setLink)
  const setActiveTab = useSettingsStore(state=>state.setActiveTab)

  const handleAppSelect = (setState: ((state: AppState) => void), appName: ApplicationName) => {
    setIsSearchBarOpen(false)
    setFocusedWindow(appName)
    setState("floating")
  }

  const apps = useApps()
  const shortcuts = useShortcuts()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsSearchBarOpen(!isSearchBarOpen)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [isSearchBarOpen, setIsSearchBarOpen])

  return (
    <CommandDialog open={isSearchBarOpen} onOpenChange={setIsSearchBarOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList className="no-scrollbar">
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Apps">
          {apps.map(({ setState, minimalIcon, name }) => (
            <CommandItem key={name} onSelect={() => { setActiveTab("general"); handleAppSelect(setState, name) }}>{minimalIcon} {name}</CommandItem>
          ))}
          {shortcuts.map(({ onClickHandler, minimalIcon, name }) => (
            <CommandItem key={name} onSelect={() => { setIsSearchBarOpen(false); onClickHandler() }}>{minimalIcon} {name}</CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Settings">
          <CommandItem onSelect={() => {
            setActiveTab("general")
            handleAppSelect(setSettingsState, "Settings")
          }} >
            <MdSettings /> General
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("wallpapers")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <FaBrush /> Wallpapers
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("music")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <FaMusic /> Music settings
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("chat")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <IoChatbubble /> Chat settings
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("appearance")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <FaPalette /> Appearance
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("widgets")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <MdWidgets /> Widgets
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setActiveTab("about")
              handleAppSelect(setSettingsState, "Settings")
            }}
          >
            <MdPerson4 /> About
          </CommandItem>
        </CommandGroup>
        <CommandGroup heading="Project Gallery">
          {projects.map(({ description, website, github, title }, index) => (
            <CommandItem
              key={index}
              className="relative"
              onSelect={() => {
                handleAppSelect(setBrowserState, "Browser")
                setLink(`https://dhvanitmonpara.in/projects?tab=${title.replace(" ", "+")}`)
              }}
            >
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium leading-snug">{title}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{description}</p>
                <a onClick={e => e.stopPropagation()} href={website || github} className="absolute p-3 top-3 right-3 rounded-full text-zinc-500 dark:text-zinc-400 hover:bg-zinc-600 dark:hover:bg-zinc-300 hover:text-zinc-200 dark:hover:text-zinc-900" target="_blank" rel="noopener noreferrer">
                  <FiArrowUpRight />
                </a>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Games">
          {games.map(({ title, icon, link }) => (
            <CommandItem
              key={title}
              onSelect={() => {
                handleAppSelect(setBrowserState, "Browser")
                setLink(link)
              }}
            >
              {icon} {title}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Contact">
          <CommandItem
            onSelect={() => {
              window.open("https://github.com/dhvanitmonpara", "_blank", "noopener,noreferrer")
            }}
          >
            <FaGithub /> Github
          </CommandItem>
          <CommandItem
            onSelect={() => {
              window.open("https://www.linkedin.com/in/dhvanitmonpara/", "_blank", "noopener,noreferrer")
            }}
          >
            <FaLinkedin /> LinkedIn
          </CommandItem>
          <CommandItem
            onSelect={() => {
              window.open("https://x.com/dhvanitdev", "_blank", "noopener,noreferrer")
            }}
          >
            <FaXTwitter /> X - Formally Twitter
          </CommandItem>
          <CommandItem
            onSelect={() => {
              window.open(`https://os.dhvanitmonpara.in/source-code`, "_blank", "noopener,noreferrer")
            }}
          >
            <FaCode /> Source Code
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}

export default SearchBar